import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
// HelmetProvider is mounted once at the root in index.js - do not nest a second
// one here, or head updates from this subtree are tracked by the inner provider
// and can fail to reach the document.
import { Helmet } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import performanceMonitor from './utils/performance';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';

// UI Components
import ScrollToTop from './components/ui/ScrollToTop';
import AdScripts from './components/common/AdScripts';
import AdminRoute from './components/auth/AdminRoute';
import UserRoute from './components/auth/UserRoute';

// Context
import { AuthModalProvider, useAuthModal } from './contexts/AuthModalContext';
import { useAuth } from './contexts/AuthContext';

// Pages (Lazy Loaded for Instant Initial Page Load & Minimal Bundle Size)
const AdminNav = React.lazy(() => import('./components/layout/AdminNav'));
const SearchModal = React.lazy(() => import('./components/ui/SearchModal'));
const AuthModal = React.lazy(() => import('./components/ui/AuthModal'));
const JobsPage = React.lazy(() => import('./pages/JobsPage'));
const FilteredJobsPage = React.lazy(() => import('./pages/FilteredJobsPage'));
const AdmitCardsPage = React.lazy(() => import('./pages/AdmitCardsPage'));
const ResultsPage = React.lazy(() => import('./pages/ResultsPage'));
const AnswerKeyPage = React.lazy(() => import('./pages/AnswerKeyPage'));
const ScholarshipsPage = React.lazy(() => import('./pages/ScholarshipsPage'));
const YojanaPage = React.lazy(() => import('./pages/YojanaPage'));
const ExamsCalendarPage = React.lazy(() => import('./pages/ExamsCalendarPage'));

// Legal & Info Pages (Lazy Loaded)
const AboutUsPage = React.lazy(() => import('./pages/AboutUsPage'));
const DeclarationPage = React.lazy(() => import('./pages/DeclarationPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const RefundPolicyPage = React.lazy(() => import('./pages/RefundPolicyPage'));
const ContactUsPage = React.lazy(() => import('./pages/ContactUsPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const BrandIdentityPage = React.lazy(() => import('./pages/BrandIdentityPage'));
const DocsPage = React.lazy(() => import('./pages/DocsPage'));

// Detail Pages (Lazy Loaded)
const JobDetailFromApi = React.lazy(() => import('./pages/JobDetailFromApi'));
const AdmitCardDetailFromApi = React.lazy(() => import('./pages/AdmitCardDetailFromApi'));
const ResultDetailFromApi = React.lazy(() => import('./pages/ResultDetailFromApi'));
const AnswerKeyDetailFromApi = React.lazy(() => import('./pages/AnswerKeyDetailFromApi'));
const ScholarshipDetailFromApi = React.lazy(() => import('./pages/ScholarshipDetailFromApi'));
const YojanaDetailFromApi = React.lazy(() => import('./pages/YojanaDetailFromApi'));
const ExamCalendarDetailFromApi = React.lazy(() => import('./pages/ExamCalendarDetailFromApi'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// User Pages (Lazy Loaded)
const UserDashboard = React.lazy(() => import('./pages/UserDashboard'));
const SmartAlertsPage = React.lazy(() => import('./pages/SmartAlertsPage'));
const BookmarksPage = React.lazy(() => import('./pages/BookmarksPage'));
const PremiumPlansPage = React.lazy(() => import('./pages/PremiumPlansPage'));

// Admin Pages (Lazy Loaded)
const ExamCalendarListPage = React.lazy(() => import('./pages/admin/ExamCalendarListPage'));
const AddEditExamCalendarPage = React.lazy(() => import('./pages/admin/AddEditExamCalendarPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminLoginPage = React.lazy(() => import('./pages/admin/AdminLoginPage'));
const JobsListPage = React.lazy(() => import('./pages/admin/JobsListPage'));
const AddJobPage = React.lazy(() => import('./pages/admin/AddJobPage'));
const AddAdmitCardPage = React.lazy(() => import('./pages/admin/AddAdmitCardPage'));
const AdmitCardsListPage = React.lazy(() => import('./pages/admin/AdmitCardsListPage'));
const EditJobPage = React.lazy(() => import('./pages/admin/EditJobPage'));
const EditAdmitCardPage = React.lazy(() => import('./pages/admin/EditAdmitCardPage'));
const AddResultPage = React.lazy(() => import('./pages/admin/AddResultPage'));
const EditResultPage = React.lazy(() => import('./pages/admin/EditResultPage'));
const AddAnswerKeyPage = React.lazy(() => import('./pages/admin/AddAnswerKeyPage'));
const EditAnswerKeyPage = React.lazy(() => import('./pages/admin/EditAnswerKeyPage'));
const ResultsListPage = React.lazy(() => import('./pages/admin/ResultsListPage'));
const AnswerKeysListPage = React.lazy(() => import('./pages/admin/AnswerKeysListPage'));
const AddScholarshipPage = React.lazy(() => import('./pages/admin/AddScholarshipPage'));
const EditScholarshipPage = React.lazy(() => import('./pages/admin/EditScholarshipPage'));
const ScholarshipsListPage = React.lazy(() => import('./pages/admin/ScholarshipsListPage'));
const YojanaListPage = React.lazy(() => import('./pages/admin/YojanaListPage'));
const AddEditYojanaPage = React.lazy(() => import('./pages/admin/AddEditYojanaPage'));
const ExpiredNotificationsPage = React.lazy(() => import('./pages/admin/ExpiredNotificationsPage'));
const TrashPage = React.lazy(() => import('./pages/admin/TrashPage'));
const AIQueuePage = React.lazy(() => import('./pages/admin/AIQueuePage'));
const AdminBotPage = React.lazy(() => import('./pages/admin/AdminBotPage'));
const AdsListPage = React.lazy(() => import('./pages/admin/AdsListPage'));
const AddEditAdPage = React.lazy(() => import('./pages/admin/AddEditAdPage'));
const AnalyticsPage = React.lazy(() => import('./pages/admin/AnalyticsPage'));
const ManagePremiumPlansPage = React.lazy(() => import('./pages/admin/ManagePremiumPlansPage'));
const ManageCouponsPage = React.lazy(() => import('./pages/admin/ManageCouponsPage'));
const AddEditCouponPage = React.lazy(() => import('./pages/admin/AddEditCouponPage'));

const StudioToQueue = () => {
  const [params] = useSearchParams();
  const queueId = params.get('queueId');
  return <Navigate to={queueId ? '/admin/ai-queue?queueId=' + encodeURIComponent(queueId) : '/admin/ai-queue'} replace />;
};

// There are no standalone /login and /register pages - authentication happens
// in the global AuthModal. These routes must still resolve, because
// UserRoute, api.js's 401 handler and external links all send logged-out
// users to /login, which previously landed on the 404 page.
const AuthModalRedirect = ({ mode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { openLoginModal, openRegisterModal } = useAuthModal();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (mode === 'register') {
      openRegisterModal();
    } else {
      openLoginModal();
    }
    navigate('/', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  return null;
};

// The authentication form is sizeable and hidden for almost every page view.
// Do not download or evaluate it until the context says it is actually open.
const LazyAuthModal = () => {
  const { isModalOpen } = useAuthModal();
  if (!isModalOpen) return null;

  return (
    <React.Suspense fallback={null}>
      <AuthModal />
    </React.Suspense>
  );
};

function App() {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');

  // SearchModal was mounted with no props, so its `if (!isOpen) return null`
  // fired on every render and the state + keyword filter was unreachable.
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Close the search overlay on navigation, otherwise it stays open over the
  // destination page after a result is clicked.
  useEffect(() => {
    setIsSearchOpen(false);

    // Track SPA Pageview in Google Analytics 4
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title || 'Sarkari Adda 24x7',
      });
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Start performance monitoring
    performanceMonitor.mark('app-start');
    
    // Mark app as loaded
    window.addEventListener('load', () => {
      performanceMonitor.mark('app-loaded');
      performanceMonitor.measure('app-load-time', 'app-start', 'app-loaded');
    });
  }, []);

  return (
      <AuthModalProvider>
        <div className="min-h-screen bg-gov-paper flex flex-col">
          {isAdminPath && (
            <Helmet>
              <meta name="robots" content="noindex, nofollow" />
            </Helmet>
          )}
          <ScrollToTop />
          <AdScripts />
          {isAdminPath && location.pathname !== '/admin/login' ? (
            <React.Suspense fallback={<div className="h-[70px] border-b-[2.5px] border-blue-600 bg-white" aria-hidden="true" />}>
              <AdminNav />
            </React.Suspense>
          ) : (
            <Header onOpenSearch={() => setIsSearchOpen(true)} />
          )}
          
          <main className="flex-1">
            <React.Suspense fallback={
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-600 border-t-transparent"></div>
              </div>
            }>
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/jobs" element={<JobsPage />} />
              {/* Taxonomy landing pages. These are two-segment paths, so they are
                  matched before the single-segment /jobs/:idSlug detail route
                  and cannot collide with it. */}
              <Route path="/jobs/qualification/:value" element={<FilteredJobsPage facet="qualification" />} />
              <Route path="/jobs/category/:value" element={<FilteredJobsPage facet="category" />} />
              <Route path="/jobs/state/:value" element={<FilteredJobsPage facet="state" />} />
              <Route path="/jobs/:idSlug" element={<JobDetailFromApi />} />
              <Route path="/admit-cards" element={<AdmitCardsPage />} />
              <Route path="/admit-cards/:idSlug" element={<AdmitCardDetailFromApi />} />
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/results/:idSlug" element={<ResultDetailFromApi />} />
              <Route path="/answer-key" element={<AnswerKeyPage />} />
              <Route path="/answer-key/:idSlug" element={<AnswerKeyDetailFromApi />} />
              <Route path="/scholarships" element={<ScholarshipsPage />} />
              <Route path="/scholarships/:idSlug" element={<ScholarshipDetailFromApi />} />
              <Route path="/sarkari-yojana" element={<YojanaPage />} />
              <Route path="/sarkari-yojana/:idSlug" element={<YojanaDetailFromApi />} />
              <Route path="/yojana" element={<YojanaPage />} />
              <Route path="/yojana/:idSlug" element={<YojanaDetailFromApi />} />
              <Route path="/exams-calendar" element={<ExamsCalendarPage />} />
              <Route path="/exams-calendar/:idSlug" element={<ExamCalendarDetailFromApi />} />
              <Route path="/exam-calendar" element={<ExamsCalendarPage />} />
              <Route path="/exam-calendar/:idSlug" element={<ExamCalendarDetailFromApi />} />
              
              {/* Legal & Info Pages */}
              <Route path="/about" element={<AboutUsPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/declaration" element={<DeclarationPage />} />
              <Route path="/disclaimer" element={<DeclarationPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/refund" element={<RefundPolicyPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              <Route path="/contact-us" element={<ContactUsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              {/* Brand & Search Routes */}
              <Route path="/sarkari-adda" element={<BrandIdentityPage />} />
              <Route path="/brand" element={<BrandIdentityPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/developers" element={<DocsPage />} />
              <Route path="/search" element={<JobsPage />} />
              
              {/* User Auth & Dashboard */}
              <Route path="/login" element={<AuthModalRedirect mode="login" />} />
              <Route path="/register" element={<AuthModalRedirect mode="register" />} />
              <Route path="/dashboard" element={<UserRoute><UserDashboard /></UserRoute>} />
              <Route path="/smart-alerts" element={<UserRoute><SmartAlertsPage /></UserRoute>} />
              <Route path="/bookmarks" element={<UserRoute><BookmarksPage /></UserRoute>} />
              <Route path="/premium-plans" element={<PremiumPlansPage />} />
              <Route path="/premium" element={<PremiumPlansPage />} />
              
              {/* Detail Pages */}
              {/* Legacy alias. The param must be named idSlug: the component
                  reads useParams().idSlug, so a ":id" param left it undefined
                  and the page emitted canonical=/admit-cards/undefined. */}
              <Route path="/admit-card-details/:idSlug" element={<AdmitCardDetailFromApi />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
              <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />

              <Route path="/admin/jobs" element={<AdminRoute><JobsListPage /></AdminRoute>} />
              <Route path="/admin/jobs/add" element={<AdminRoute><AddJobPage /></AdminRoute>} />
              <Route path="/admin/jobs/:id/edit" element={<AdminRoute><EditJobPage /></AdminRoute>} />
              <Route path="/admin/admit-cards" element={<AdminRoute><AdmitCardsListPage /></AdminRoute>} />
              <Route path="/admin/admit-cards/add" element={<AdminRoute><AddAdmitCardPage /></AdminRoute>} />
              <Route path="/admin/admit-cards/:id/edit" element={<AdminRoute><EditAdmitCardPage /></AdminRoute>} />
              <Route path="/admin/results/add" element={<AdminRoute><AddResultPage /></AdminRoute>} />
              <Route path="/admin/results" element={<AdminRoute><ResultsListPage /></AdminRoute>} />
              <Route path="/admin/results/:id/edit" element={<AdminRoute><EditResultPage /></AdminRoute>} />
              <Route path="/admin/answer-keys/add" element={<AdminRoute><AddAnswerKeyPage /></AdminRoute>} />
              <Route path="/admin/answer-keys" element={<AdminRoute><AnswerKeysListPage /></AdminRoute>} />
              <Route path="/admin/answer-keys/:id/edit" element={<AdminRoute><EditAnswerKeyPage /></AdminRoute>} />
              <Route path="/admin/scholarships" element={<AdminRoute><ScholarshipsListPage /></AdminRoute>} />
              <Route path="/admin/scholarships/add" element={<AdminRoute><AddScholarshipPage /></AdminRoute>} />
              <Route path="/admin/scholarships/:id/edit" element={<AdminRoute><EditScholarshipPage /></AdminRoute>} />
              {/* Sarkari Yojana. /add is a literal segment and React Router
                  ranks it above the :id of /admin/yojana/:id/edit, so a scheme
                  can never be edited at the id "add". */}
              <Route path="/admin/yojana" element={<AdminRoute><YojanaListPage /></AdminRoute>} />
              <Route path="/admin/yojana/add" element={<AdminRoute><AddEditYojanaPage /></AdminRoute>} />
              <Route path="/admin/yojana/:id/edit" element={<AdminRoute><AddEditYojanaPage /></AdminRoute>} />
              <Route path="/admin/expired" element={<AdminRoute><ExpiredNotificationsPage /></AdminRoute>} />
              <Route path="/admin/trash" element={<AdminRoute><TrashPage /></AdminRoute>} />

              {/* AI Scraper queue. Old Studio URLs redirect here. */}
              <Route path="/admin/ai-queue" element={<AdminRoute><AIQueuePage /></AdminRoute>} />
              <Route path="/admin/ai-scraper" element={<AdminRoute><AIQueuePage /></AdminRoute>} />
              <Route path="/admin/scraper" element={<AdminRoute><AIQueuePage /></AdminRoute>} />
              <Route path="/admin/ai-studio" element={<AdminRoute><StudioToQueue /></AdminRoute>} />
              <Route path="/admin/ai-notification-studio" element={<AdminRoute><StudioToQueue /></AdminRoute>} />
              <Route path="/admin/bot" element={<AdminRoute><AdminBotPage /></AdminRoute>} />
              <Route path="/admin/adda-bot" element={<AdminRoute><AdminBotPage /></AdminRoute>} />

              {/* Direct Path Aliases */}
              <Route path="/ai-studio" element={<Navigate to="/admin/ai-queue" replace />} />
              <Route path="/ai-scraper" element={<Navigate to="/admin/ai-scraper" replace />} />
              <Route path="/ai-queue" element={<Navigate to="/admin/ai-queue" replace />} />
              <Route path="/scraper" element={<Navigate to="/admin/ai-scraper" replace />} />

              <Route path="/admin/analytics" element={<AdminRoute><AnalyticsPage /></AdminRoute>} />
              <Route path="/admin/ads" element={<AdminRoute><AdsListPage /></AdminRoute>} />
              <Route path="/admin/ads/add" element={<AdminRoute><AddEditAdPage /></AdminRoute>} />
              <Route path="/admin/ads/:id/edit" element={<AdminRoute><AddEditAdPage /></AdminRoute>} />
              <Route path="/admin/exam-calendar" element={<AdminRoute><ExamCalendarListPage /></AdminRoute>} />
              <Route path="/admin/exam-calendar/add" element={<AdminRoute><AddEditExamCalendarPage /></AdminRoute>} />
              <Route path="/admin/exam-calendar/:id/edit" element={<AdminRoute><AddEditExamCalendarPage /></AdminRoute>} />
              <Route path="/admin/premium-plans" element={<AdminRoute><ManagePremiumPlansPage /></AdminRoute>} />
              <Route path="/admin/coupons" element={<AdminRoute><ManageCouponsPage /></AdminRoute>} />
              <Route path="/admin/coupons/add" element={<AdminRoute><AddEditCouponPage /></AdminRoute>} />
              <Route path="/admin/coupons/:id/edit" element={<AdminRoute><AddEditCouponPage /></AdminRoute>} />

              {/* 404 Catch-All Route */}
              <Route path="*" element={<NotFoundPage />} />
             </Routes>
            </React.Suspense>
          </main>
          
          <Footer />
          
          {/* Modals */}
          {isSearchOpen && (
            <React.Suspense fallback={null}>
              <SearchModal isOpen onClose={() => setIsSearchOpen(false)} />
            </React.Suspense>
          )}
          <LazyAuthModal />
          
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            containerStyle={{
              top: 24,
              right: 24,
              zIndex: 99999,
            }}
            toastOptions={{
              duration: 3500,
              className: 'sa-toast-custom',
              style: {
                background: '#ffffff',
                color: '#0f172a',
                fontFamily: "'Inter', sans-serif",
                fontSize: '12.5px',
                fontWeight: '600',
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
                maxWidth: '440px',
              },
              success: {
                duration: 3000,
                style: {
                  background: '#ffffff',
                  color: '#0f172a',
                  border: '1px solid #bbf7d0',
                  boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
                },
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                duration: 4500,
                style: {
                  background: '#ffffff',
                  color: '#0f172a',
                  border: '1px solid #fecdd3',
                  boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
                },
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
              loading: {
                style: {
                  background: '#ffffff',
                  color: '#0f172a',
                  border: '1px solid #fed7aa',
                },
                iconTheme: {
                  primary: '#f97316',
                  secondary: '#ffffff',
                },
              },
            }}
          />
        </div>
      </AuthModalProvider>
  );
}

export default App;
